import { createContext, useCallback, useContext, useMemo } from 'react';
import { IAutomation } from 'pages/revisions-module/template-editor/editors/pipeline/types';
import { useTemplateEditorData } from 'pages/revisions-module/template-editor/TemplateEditorDataProvider';
import { ISpecialPlaceholder } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/Placeholder';

interface IPipelineDataContext {
  automations: IAutomation[];
  placeholderGroups: { name: string; placeholders: ISpecialPlaceholder[] }[];
  mutateAutomations: (mutate: (automations: IAutomation[]) => IAutomation[]) => void;
  addAutomation: (automation: IAutomation) => void;
  removeAutomation: (automationId: string) => void;
  updateAutomation: (automation: IAutomation) => void;
  moveAutomationAfter: (id: string, afterId: string) => void;
  moveAutomationToStart: (id: string) => void;
}

const PipelineDataContext = createContext<IPipelineDataContext>(undefined!);

/**
 * Provides the pipeline data to the pipeline editor.
 */
export function PipelineDataProvider({ children }: { children: React.ReactNode }) {
  const {
    pipeline: { automations },
    setPipeline,
  } = useTemplateEditorData();

  const placeholderGroups = useMemo(() => {
    const groups: { name: string; placeholders: ISpecialPlaceholder[] }[] = [];

    for (const automation of automations) {
      if (automation.type === 'gather-confirmation') {
        groups.push({
          name: `Potvrdenie - ${automation.label}`,
          placeholders: [
            {
              context: 'revision',
              name: `_confirmations.${automation.id}.name`,
              label: `Meno potvrdzujúceho (${automation.label})`,
              type: 'confirmationProperty',
            },
            {
              context: 'revision',
              name: `_confirmations.${automation.id}.date`,
              label: `Dátum potvrdenia (${automation.label})`,
              type: 'confirmationProperty',
            },
            {
              context: 'revision',
              name: `_confirmations.${automation.id}.time`,
              label: `Čas potvrdenia (${automation.label})`,
              type: 'confirmationProperty',
            },
            {
              context: 'revision',
              name: `_confirmations.${automation.id}.signature`,
              label: `Podpis (${automation.label})`,
              type: 'confirmationSignature',
            },
          ],
        });
      }
    }

    return groups;
  }, [automations]);

  const mutateAutomations = useCallback((mutate: (automations: IAutomation[]) => IAutomation[]) => {
    setPipeline((pipeline) => ({ ...pipeline, automations: mutate(pipeline.automations) }));
  }, []);

  const addAutomation = useCallback((automation: IAutomation) => {
    mutateAutomations((automations) => [...automations, automation]);
  }, []);

  const removeAutomation = useCallback((automationId: string) => {
    mutateAutomations((automations) => automations.filter((automation) => automation.id !== automationId));
  }, []);

  const updateAutomation = useCallback((automation: IAutomation) => {
    mutateAutomations((automations) =>
      automations.map((existingAutomation) =>
        existingAutomation.id === automation.id ? automation : existingAutomation
      )
    );
  }, []);

  const moveAutomationAfter = useCallback((id: string, afterId: string) => {
    mutateAutomations((automations) => {
      const sourceIndex = automations.findIndex((automation) => automation.id === id);
      const targetIndex = automations.findIndex((automation) => automation.id === afterId);

      if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
        return automations;
      }

      const newAutomations = [...automations];
      const [sourceAutomation] = newAutomations.splice(sourceIndex, 1);
      newAutomations.splice(targetIndex + 1, 0, sourceAutomation);
      return newAutomations;
    });
  }, []);

  const moveAutomationToStart = useCallback((id: string) => {
    mutateAutomations((automations) => {
      const sourceIndex = automations.findIndex((automation) => automation.id === id);

      if (sourceIndex === -1) {
        return automations;
      }

      const newAutomations = [...automations];
      const [sourceAutomation] = newAutomations.splice(sourceIndex, 1);
      newAutomations.unshift(sourceAutomation);
      return newAutomations;
    });
  }, []);

  const value = useMemo(
    () => ({
      automations,
      placeholderGroups,
      mutateAutomations,
      addAutomation,
      removeAutomation,
      updateAutomation,
      moveAutomationAfter,
      moveAutomationToStart,
    }),
    [
      automations,
      placeholderGroups,
      mutateAutomations,
      addAutomation,
      removeAutomation,
      updateAutomation,
      moveAutomationAfter,
      moveAutomationToStart,
    ]
  );

  return <PipelineDataContext.Provider value={value}>{children}</PipelineDataContext.Provider>;
}

/**
 * Use the pipeline data from the pipeline editor.
 */
export function usePipelineData() {
  return useContext(PipelineDataContext);
}
